import { AsyncPipe, DatePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnInit
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { InputComponent } from "@components/input/input.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import _ from "lodash";
import {
    map,
    merge,
    Observable,
    scan,
    share,
    shareReplay,
    Subject,
    switchMap,
    tap,
    withLatestFrom
} from "rxjs";
import { EudrRaskStatuses } from "~app/common/models/constants";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { EudrAnmalanVM } from "~app/features/planera/arbetsorder-info/EUDR/eudr-status/EudrAnmalanVM";
import { SetEjEudrCommand } from "~app/features/planera/commands/SetEjEudrCommand";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { EudrService } from "~app/features/planera/services/eudr.service";
import { PlaneraService } from "~app/features/planera/services/planera.service";

type EudrAnmalanState = {
    items: EudrAnmalanVM[];
    loading: boolean;
    canSubmit: boolean;
    canAmend: boolean;
    canRetract: boolean;
};

type EudrAnmalanReducer = (curr: EudrAnmalanState) => EudrAnmalanState;

@Component({
    selector: "rask-sysadm-eudr",
    imports: [
        AsyncPipe,
        DatePipe,
        RaskCommonModule,
        BlockComponent,
        InputComponent,
        FormsModule,
        ButtonComponent,
        TextareaComponent
    ],
    templateUrl: "./sysadm-eudr.component.html",
    styleUrl: "./sysadm-eudr.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SysadmEudrComponent implements OnInit {
    referenceNumber = "";
    verificationNumber = "";
    resultat = "";
    beteckning = "";
    protected latestEudrAnmalan: EudrAnmalanState | null = null;
    protected eudrAnmalan$: Observable<EudrAnmalanState>;
    protected hamtadArbetsorder: IArbetsorderDetail | null = null;

    private readonly eudrService = inject(EudrService);
    private readonly commandService = inject(CommandService);
    private readonly arbetsorderChangeSrc = new Subject<IArbetsorderDetail>();
    private readonly submitAnmalanSrc = new Subject<void>();
    private readonly amendAnmalanSrc = new Subject<void>();
    private readonly removeAnmalanSrc = new Subject<void>();
    private readonly retrieveAnmalanSrc = new Subject<void>();
    private readonly planeraService = inject(PlaneraService);
    private readonly cdRef = inject(ChangeDetectorRef);

    private readonly arbetsorderGuid$ = this.arbetsorderChangeSrc.pipe(
        map(ao => ao.guid),
        shareReplay(1)
    );

    private readonly reload$ = this.arbetsorderGuid$.pipe(
        switchMap(guid => this.eudrService.getAll(guid)),
        map(res => res.map(r => new EudrAnmalanVM(r)))
    );
    private readonly submitAnmalan$ = this.submitAnmalanSrc.pipe(
        withLatestFrom(this.arbetsorderGuid$),
        switchMap(([_, guid]) => this.eudrService.submit(guid)),
        share()
    );
    private readonly amendAnmalan$ = this.amendAnmalanSrc.pipe(
        withLatestFrom(this.arbetsorderGuid$),
        switchMap(([_, guid]) => this.eudrService.amend(guid)),
        share()
    );
    private readonly removeAnmalan$ = this.removeAnmalanSrc.pipe(
        withLatestFrom(this.arbetsorderGuid$),
        switchMap(([_, guid]) => this.eudrService.remove(guid)),
        share()
    );
    private readonly retrieveAnmalan$ = this.retrieveAnmalanSrc.pipe(
        switchMap(() => this.arbetsorderGuid$)
    );

    private readonly fetchReducer: Observable<EudrAnmalanReducer> = merge(
        this.reload$,
        this.submitAnmalan$,
        this.amendAnmalan$,
        this.removeAnmalan$,
        this.retrieveAnmalan$
    ).pipe(
        switchMap(() => this.reload$),
        map((res) => (curr) => ({
            ...curr,
            loading: false,
            items: res,
            canSubmit: this.canSubmit(res),
            canAmend: this.canAmend(res),
            canRetract: this.canRetract(res)
        }))
    );

    private readonly loadingReducer: Observable<EudrAnmalanReducer> = merge(
        this.submitAnmalanSrc,
        this.amendAnmalanSrc,
        this.removeAnmalanSrc,
        this.retrieveAnmalanSrc,
        this.arbetsorderGuid$
    ).pipe(
        map((_res) => (curr) => ({
            ...curr,
            loading: true
        }))
    );

    getArbetsorder(): void {
        if (!this.beteckning?.trim()) {
            return;
        }

        this.planeraService.getArbetsorderIdFromBeteckning(this.beteckning).pipe(
            switchMap(aoRes => this.planeraService.getArbetsorderDetail(aoRes.id)),
            map(detailResponse => detailResponse.arbetsorder),
            tap(arbetsorder => {
                this.hamtadArbetsorder = arbetsorder;
                if (!arbetsorder.commandManager) {
                    arbetsorder.commandManager = new CommandManager();
                }
                this.arbetsorderChangeSrc.next(arbetsorder);
            })
        ).subscribe({
            error: (error) => {
                console.error("Fel vid hämtning av arbetsorder:", error);
                this.hamtadArbetsorder = null;
            }
        });
    }

    getStatementByIdentifiers() {
        this.eudrService.getStatementByIdentifier(this.referenceNumber, this.verificationNumber).subscribe(x => {
            this.resultat = JSON.stringify(x, null, 2);
            this.cdRef.detectChanges();
        });
    }

    setEjEudr = (ejEudr: boolean) => {
        const cmd = new SetEjEudrCommand((instance) => {
            instance.arbetsorderId = this.hamtadArbetsorder.id;
            instance.ejEudr = ejEudr;
        });

        this.commandService.addTransactionCommand(cmd);
    };

    canSave(): boolean {
        return this.commandService.isDirty();
    }

    saveChanges(): void {
        const obs = this.commandService.pushCommandQueueTransaction();
        obs.subscribe(x => {
            this.commandService.reset();
        });

    }

    ngOnInit(): void {
        this.eudrAnmalan$ = merge(
            this.fetchReducer,
            this.loadingReducer
        ).pipe(
            scan((acc: EudrAnmalanState, mutatorFn: EudrAnmalanReducer) => mutatorFn(acc),
                {
                    items: [],
                    loading: false,
                    canSubmit: true,
                    canAmend: false,
                    canRetract: false
                }),
            tap(state => this.latestEudrAnmalan = state)
        );
    }

    get disableFetchArbetsorder(): boolean {
        return (
            !this.beteckning?.trim() ||
            (this.hamtadArbetsorder && this.hamtadArbetsorder.beteckning === this.beteckning.trim())
        );
    }

    protected onSubmitClick() {
        if (this.hamtadArbetsorder) {
            this.submitAnmalanSrc.next();
        }
    }

    protected onAmendClick() {
        if (this.hamtadArbetsorder) {
            this.amendAnmalanSrc.next();
        }
    }

    protected onRemoveClick() {
        if (this.hamtadArbetsorder) {
            this.removeAnmalanSrc.next();
        }
    }

    private canSubmit(anmalanLista: EudrAnmalanVM[]): boolean {
        // Om ingen rad finns så tillåt
        if (anmalanLista.length === 0) {
            return true;
        }

        const latestRow = this.getLatestRow(anmalanLista);

        // Om senaste raden är återkallad så tillåt
        if (latestRow.status === EudrRaskStatuses.withdrawn) {
            return true;
        }

        // Om senaste raden saknar identifierare eller är i status inskickad så tillåt inte

        if ((latestRow.status === EudrRaskStatuses.submitted ||
            latestRow.identifierare === null) && latestRow.status !== null
        ) {
            return false;
        }
        return true;
    }

    private canAmend(anmalanLista: EudrAnmalanVM[]): boolean {
        // Om ingen rad finns så tillåt inte
        if (anmalanLista.length === 0) {
            return false;
        }
        const latestRow = this.getLatestRow(anmalanLista);
        // Om senaste raden har identifierare och statusen inte är submitted så tillåt

        if (latestRow.identifierare && latestRow.status !== EudrRaskStatuses.submitted) {
            return true;
        }

        return false;
    }

    private canRetract(anmalanLista: EudrAnmalanVM[]): boolean {
        // Om senaste raden har identifierare så tillåt
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (anmalanLista.length > 0 && this.getLatestRow(anmalanLista).identifierare) {
            return true;
        }
        return false;
    }

    private getLatestRow(anmalanLista: EudrAnmalanVM[]) {
        return _.orderBy(anmalanLista, ["skickad"], "desc")[0];
    }

    get disableSubmit(): boolean {
        return !this.hamtadArbetsorder || this.latestEudrAnmalan?.loading || !this.latestEudrAnmalan?.canSubmit;
    }

    get disableAmend(): boolean {
        return !this.hamtadArbetsorder || this.latestEudrAnmalan?.loading || !this.latestEudrAnmalan?.canAmend;
    }

    get disableRetract(): boolean {
        return !this.hamtadArbetsorder || this.latestEudrAnmalan?.loading || !this.latestEudrAnmalan?.canRetract;
    }
}
